import SharedDialog from '../../../shared/shared-dialog';
import { handleErrors } from '../../../utils/errors';
import {
  UserKeys,
  getResourceCalendarAcls,
  updateResourceCalendarAcls,
} from '../../../api/users-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarAclResource, ResourceCalendar } from '../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import SharedAutocomplete from '../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import UserAvatar from '../../../shared/user-avatar';
import { useChangeLogGate } from '../../../shared/hooks/use-change-log-gate';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Clear from '@mui/icons-material/Clear';

interface ResourceCalendarShareDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedCalendar: ResourceCalendar;
  setSelectedCalendar: Dispatch<SetStateAction<ResourceCalendar | null>>;
  calendarRoles: string[];
  changeLogRoles: string[];
  viewChangeLogMessage: string;
  primaryDomain: string;
  loggedInUser: string;
}

export default function ResourceCalendarShareDialog({
  open,
  handleClose,
  selectedCalendar,
  setSelectedCalendar,
  calendarRoles,
  changeLogRoles,
  viewChangeLogMessage,
  primaryDomain,
  loggedInUser,
}: ResourceCalendarShareDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [inputValue, setInputValue] = useState('');
  const { setSnackbar } = useStore();
  const changeLogGateEdit = useChangeLogGate({
    permissionValue: CalendarRoles.EditShareOfResourceCalendar.value,
    changeLogRoles,
    initialOpen: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      UserKeys.ResourceCalendarAcls,
      selectedCalendar.resourceId,
      viewChangeLogMessage,
    ],
    queryFn: () =>
      getResourceCalendarAcls(selectedCalendar, viewChangeLogMessage),
    meta: {
      errorMessage:
        'There was an issue getting the calendars permissions. Please refresh and try again.',
    },
  });

  const { mutate, isPending: isSharing } = useMutation({
    mutationFn: (data: {
      aclResources: CalendarAclResource[];
      changeLogMessage: string;
    }) =>
      updateResourceCalendarAcls(
        loggedInUser,
        selectedCalendar,
        data.aclResources,
        data.changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar share permissions updated successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.ResourceCalendarAcls, selectedCalendar.resourceId],
        refetchType: 'none',
      });

      changeLogGateEdit.reset();
      handleClose();
      setSelectedCalendar(null);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the sharing permissions of this calendar. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.calendars.resource_calendars.update_calendar_shares,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections.resourceCalendars,
        }
      );
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields },
    getValues,
    watch,
  } = useForm({
    values: {
      aclResources: data || [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'aclResources',
  });
  // Function to check if any value in an object is true, needed to see which values in resource array
  // have been changed.
  function anyValueTrue(obj: object) {
    return Object.values(obj).some((value) => value === true);
  }

  function mutateData() {
    const editedEntriesIndices = dirtyFields.aclResources!.reduce(
      (acc: number[], obj, index) => {
        if (anyValueTrue(obj)) {
          acc.push(index);
        }
        return acc;
      },
      []
    );

    const formIds = getValues('aclResources').map((acl) => acl.id);

    if (!data) return;

    // update toBeDeleted flag
    const updatedData = data.map((acl) => {
      if (!formIds.includes(acl.id)) {
        return {
          ...acl,
          toBeDeleted: true,
        };
      } else {
        const updatedAcl =
          getValues('aclResources').find((a) => a.id === acl.id) || acl;
        return updatedAcl;
      }
    });

    // add any new users
    const newUsers = getValues('aclResources').filter(
      (acl) => !data.find((a) => a.id === acl.id)
    );
    updatedData.push(...newUsers);

    // Access changes from updatedData using indices
    const filteredChanges = editedEntriesIndices.map(
      (index) => updatedData[index]
    );

    return filteredChanges;
  }

  const onSubmit: SubmitHandler<{
    aclResources: CalendarAclResource[];
  }> = (formData) => {
    const initialAcls =
      data?.filter(
        (acl) => acl.scope.type === 'user' || acl.scope.type === 'group'
      ) || [];
    const hasRemovedShare = initialAcls.some(
      (initialAcl) =>
        !formData.aclResources.some(
          (currentAcl) => currentAcl.id === initialAcl.id
        )
    );
    if (
      (changeLogGateEdit.needsChangeLog &&
        !changeLogGateEdit.confirmedChangeLogMessage.trim()) ||
      (isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.RemoveShareFromResourceCalendar.value
      ) &&
        hasRemovedShare)
    ) {
      changeLogGateEdit.openDialog();
    } else {
      const filteredData = mutateData()!;
      mutate({
        aclResources: filteredData,
        changeLogMessage: changeLogGateEdit.confirmedChangeLogMessage,
      });
    }
  };

  const domainAcl = watch('aclResources').find(
    (acl) => acl.scope.type === 'domain'
  );
  const publicAcl = watch('aclResources').find(
    (acl) => acl.scope.type === 'default_public'
  );

  const roleOptions = [
    {
      displayValue: 'See Only Free/Busy',
      value: 'freeBusyReader',
    },
    {
      displayValue: 'See All Events Details',
      value: 'reader',
    },
  ];

  return (
    <>
      <ChangeLogDialog
        open={changeLogGateEdit.openChangeLogDialog}
        handleClose={() => changeLogGateEdit.dismiss()}
        changeLogMessage={changeLogGateEdit.changeLogMessage}
        setChangeLogMessage={changeLogGateEdit.setChangeLogMessage}
        mutate={() => {
          const message = changeLogGateEdit.changeLogMessage;
          changeLogGateEdit.confirm();
          const filteredData = mutateData();
          if (filteredData) {
            mutate({ aclResources: filteredData, changeLogMessage: message });
          }
        }}
        mutateLoading={isSharing}
      />
      <SharedDialog
        open={open}
        handleClose={() => {
          handleClose();
          setSelectedCalendar(null);
        }}
        title="Share Resource Calendar"
        dialogContent={
          <Stack>
            {isLoading ? (
              <Stack gap={1}>
                <Skeleton variant="rounded" height={120} />
              </Stack>
            ) : (
              <Stack gap={2}>
                <Stack flexDirection={'row'} justifyContent={'space-between'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={
                          !isAbleToSeeUIElement(
                            calendarRoles,
                            CalendarRoles.EditShareOfResourceCalendar.value
                          )
                        }
                        checked={domainAcl ? true : false}
                        onChange={() => {
                          if (domainAcl) {
                            const domainIndex = fields?.findIndex(
                              (acl) => acl.scope.type === domainAcl.scope.type
                            );
                            remove(domainIndex);
                          } else {
                            append({
                              id: `domain:${primaryDomain}`,
                              canActOn: false,
                              toBeDeleted: false,
                              role: 'freeBusyReader',
                              scope: {
                                type: 'domain',
                                value: primaryDomain,
                              },
                            });
                          }
                        }}
                      />
                    }
                    sx={{ width: 240 }}
                    label="Share Calendar to Domain"
                  />
                  <TextField
                    inputProps={{
                      id: 'domainRole',
                    }}
                    InputLabelProps={{
                      htmlFor: 'domainRole',
                    }}
                    select
                    label="Domain Role"
                    disabled={
                      domainAcl &&
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.EditShareOfResourceCalendar.value
                      )
                        ? true
                        : false
                    }
                    sx={{ width: 220 }}
                    value={domainAcl?.role || ''}
                    onChange={(event) => {
                      const domainIndex = fields.findIndex(
                        (acl) => acl.scope.type === 'domain'
                      );
                      update(domainIndex, {
                        ...domainAcl!,
                        role: event.target.value as
                          | 'none'
                          | 'freeBusyReader'
                          | 'reader'
                          | 'writer'
                          | 'owner',
                      });
                    }}
                  >
                    {roleOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack flexDirection={'row'} justifyContent={'space-between'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={
                          !isAbleToSeeUIElement(
                            calendarRoles,
                            CalendarRoles.EditShareOfResourceCalendar.value
                          )
                        }
                        checked={publicAcl ? true : false}
                        onChange={() => {
                          if (publicAcl) {
                            const publicIndex = fields?.findIndex(
                              (acl) => acl.scope.type === 'default_public'
                            );
                            remove(publicIndex);
                          } else {
                            append({
                              id: `default`,
                              canActOn: false,
                              toBeDeleted: false,
                              role: 'freeBusyReader',
                              scope: {
                                type: 'default_public',
                                value:
                                  '__public_principal__@public.calendar.google.com',
                              },
                            });
                          }
                        }}
                      />
                    }
                    label="Share Calendar to Public"
                  />
                  <TextField
                    inputProps={{
                      id: 'publicRole',
                    }}
                    InputLabelProps={{
                      htmlFor: 'publicRole',
                    }}
                    select
                    label="Public Role"
                    disabled={
                      publicAcl &&
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.EditShareOfResourceCalendar.value
                      )
                        ? true
                        : false
                    }
                    sx={{ width: 220 }}
                    value={publicAcl?.role || ''}
                    onChange={(event) => {
                      const publicIndex = fields.findIndex(
                        (acl) => acl.scope.type === 'default_public'
                      );
                      update(publicIndex, {
                        ...publicAcl!,
                        role: event.target.value as
                          | 'none'
                          | 'freeBusyReader'
                          | 'reader'
                          | 'writer'
                          | 'owner',
                      });
                    }}
                  >
                    {roleOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Stack>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ px: 1 }}>
              <Stack flexDirection="row" gap={1} sx={{ mb: 1 }}>
                <Box sx={{ width: '100%' }}>
                  <SharedAutocomplete
                    sources={['users', 'groups']}
                    autocompleteValue={inputValue}
                    setAutoCompleteValue={setInputValue}
                    autoFocus
                    freeSolo
                    disabled={
                      !isAbleToSeeUIElement(
                        calendarRoles,
                        CalendarRoles.EditShareOfResourceCalendar.value
                      )
                    }
                    onSelection={(value) => {
                      if (!value) return;
                      if (fields.find((acl) => acl.scope.value === value))
                        return;
                      if (value === '') return;

                      append({
                        id: `user:${value}`,
                        canActOn: true,
                        toBeDeleted: false,
                        role: 'reader',
                        scope: {
                          type: 'user',
                          value: value,
                        },
                      });

                      setInputValue('');
                    }}
                  />
                </Box>
              </Stack>

              <Typography>Who has access:</Typography>
              {isLoading ? (
                <Stack gap={1}>
                  <Skeleton variant="rounded" height={60} />
                </Stack>
              ) : (
                <List sx={{ maxHeight: 160, overflowY: 'auto' }}>
                  {fields.map((acl, index) => {
                    if (
                      acl.scope.type !== 'default_public' &&
                      acl.scope.type !== 'domain'
                    ) {
                      return (
                        <ListItem
                          key={acl.id}
                          dense
                          secondaryAction={
                            <Stack
                              flexDirection="row"
                              gap={1}
                              alignItems="center"
                            >
                              <Controller
                                name={`aclResources.${index}.role`}
                                control={control}
                                rules={{
                                  required: 'Calendar Name is required.',
                                }}
                                render={({ field }) => (
                                  <>
                                    <TextField
                                      {...field}
                                      inputProps={{
                                        id: 'role',
                                      }}
                                      InputLabelProps={{
                                        htmlFor: 'role',
                                      }}
                                      select
                                      disabled={
                                        !isAbleToSeeUIElement(
                                          calendarRoles,
                                          CalendarRoles
                                            .EditShareOfResourceCalendar.value
                                        )
                                      }
                                      defaultValue={acl.role}
                                      label="Role"
                                      size="small"
                                      sx={{ width: 140 }}
                                    >
                                      <MenuItem key="writer" value="writer">
                                        Editor
                                      </MenuItem>
                                      <MenuItem
                                        key="freeBusyReader"
                                        value="freeBusyReader"
                                      >
                                        Free/Busy Viewer
                                      </MenuItem>
                                      <MenuItem key="owner" value="owner">
                                        Owner
                                      </MenuItem>

                                      <MenuItem key="reader" value="reader">
                                        Viewer
                                      </MenuItem>
                                      <MenuItem
                                        key="writerWithoutPrivateAccess"
                                        value="writerWithoutPrivateAccess"
                                      >
                                        Make changes (see private events as
                                        free/busy)
                                      </MenuItem>
                                    </TextField>
                                    {
                                      <Tooltip arrow title="Remove">
                                        <IconButton
                                          size="small"
                                          disabled={
                                            !isAbleToSeeUIElement(
                                              calendarRoles,
                                              CalendarRoles
                                                .EditShareOfResourceCalendar
                                                .value
                                            )
                                          }
                                          onClick={() => {
                                            const aclIndex = fields?.findIndex(
                                              (currentAcls) =>
                                                currentAcls.id === acl.id
                                            );
                                            remove(aclIndex);
                                          }}
                                        >
                                          <Clear />
                                        </IconButton>
                                      </Tooltip>
                                    }
                                  </>
                                )}
                              />
                            </Stack>
                          }
                        >
                          <ListItemAvatar>
                            <UserAvatar userEmail={acl.scope.value || ''} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={acl.scope.value}
                            primaryTypographyProps={{
                              maxWidth: 200,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                          />
                        </ListItem>
                      );
                    }
                  })}
                </List>
              )}
            </Box>
          </Stack>
        }
        primaryButton={{
          title: 'Share Resource Calendar',
          action: handleSubmit(onSubmit),
          loading: isSharing,
          disabled: isLoading || isSharing || !isDirty,
        }}
      />
    </>
  );
}
